// @ts-nocheck
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import * as api from '../services/api';
import {
  Criteria,
  Label,
  LabelType,
  NotificationState,
  SelectedFragment,
  Transcription,
  User,
} from '../types';
import { Granularity } from '../types';

/**
 * Hook for managing labels data
 */
const useLabelData = () => {
  const [labels, setLabels] = useState<Label[]>([]);

  // Fetch labels on mount
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const data = await api.fetchLabels();
        setLabels(data);
      } catch (error) {
        console.error('Error fetching labels:', error);
      }
    };

    fetchLabels();
  }, []);

  return { labels, setLabels };
};

/**
 * Hook for managing form state related to labelling
 */
const useLabelForm = () => {
  const [selectedFragment, setSelectedFragment] = useState<SelectedFragment | null>(null);
  const [labelType, setLabelType] = useState<LabelType>('positive');
  const [reason, setReason] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentLabelId, setCurrentLabelId] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setSelectedFragment(null);
    setIsEditing(false);
    setCurrentLabelId(null);
    setReason('');
    setLabelType('positive');
  }, []);

  return {
    selectedFragment,
    setSelectedFragment,
    labelType,
    setLabelType,
    reason,
    setReason,
    isEditing,
    setIsEditing,
    currentLabelId,
    setCurrentLabelId,
    resetForm,
  };
};

/**
 * Hook for handling transcription unidentifiable state
 */
const useTranscriptionIdentifiability = (
  selectedTranscription: Transcription | null,
  selectedCriteria: Criteria | null,
  labels: Label[],
  currentUser?: User | null
) => {
  const [isTranscriptionUnidentifiable, setIsTranscriptionUnidentifiable] =
    useState<boolean>(false);

  // Update transcription unidentifiable state when transcription or policy changes
  useEffect(() => {
    if (selectedTranscription && selectedCriteria && currentUser) {
      const unidentifiableLabel = labels.find(
        label =>
          label.transcriptionId === selectedTranscription.id &&
          label.criteriaId === selectedCriteria.id &&
          label.type === 'unidentifiable' &&
          label.createdBy === currentUser.id
      );
      setIsTranscriptionUnidentifiable(!!unidentifiableLabel);
    } else {
      setIsTranscriptionUnidentifiable(false);
    }
  }, [selectedTranscription, selectedCriteria, labels, currentUser]);

  return { isTranscriptionUnidentifiable, setIsTranscriptionUnidentifiable };
};

/**
 * Hook for notification utilities
 */
const useNotificationHelper = (setNotification: (notification: NotificationState) => void) => {
  const notify = useCallback(
    (message: string, severity: 'success' | 'error') => {
      setNotification({
        open: true,
        message,
        severity,
      });
    },
    [setNotification]
  );

  const handleApiError = useCallback(
    (error: any, message: string) => {
      console.error(`${message}:`, error);
      notify(`${message}`, 'error');
    },
    [notify]
  );

  return { notify, handleApiError };
};

/**
 * Hook for fragment selection and label editing interactions
 */
const useFragmentInteractions = (
  selectedTranscription: Transcription | null,
  selectedCriteria: Criteria | null,
  labels: Label[],
  isTranscriptionUnidentifiable: boolean,
  formState: ReturnType<typeof useLabelForm>,
  getCleanFragments: (text: string) => string[],
  currentUser?: User | null
) => {
  const { setSelectedFragment, setIsEditing, setCurrentLabelId, setLabelType, setReason } =
    formState;

  const getLabelForFragment = useCallback(
    (fragmentIndex: number): Label | null => {
      if (!selectedTranscription || !selectedCriteria || !currentUser) return null;

      const fragments = getCleanFragments(selectedTranscription.content);
      const fragmentText = fragmentIndex < fragments.length ? fragments[fragmentIndex] : null;

      return (
        labels.find(
          label =>
            label.transcriptionId === selectedTranscription.id &&
            label.criteriaId === selectedCriteria.id &&
            (typeof label.fragmentIndex === 'number'
              ? label.fragmentIndex === fragmentIndex
              : label.essayContentFragment === fragmentText) &&
            label.createdBy === currentUser.id
        ) || null
      );
    },
    [selectedTranscription, selectedCriteria, labels, getCleanFragments, currentUser]
  );

  const handleFragmentClick = useCallback(
    (fragment: string, index: number) => {
      if (isTranscriptionUnidentifiable || !selectedTranscription || !selectedCriteria) {
        return;
      }

      const existingLabel = getLabelForFragment(index);

      const cleanFragment = fragment.startsWith('\n\n') ? fragment.substring(2) : fragment;

      setSelectedFragment({ text: cleanFragment, index });

      if (existingLabel) {
        setIsEditing(true);
        setCurrentLabelId(existingLabel.id);
        setLabelType(existingLabel.type);
        setReason(existingLabel.reason || '');
      } else {
        setIsEditing(false);
        setCurrentLabelId(null);
        setLabelType('positive');
        setReason('');
      }
    },
    [
      isTranscriptionUnidentifiable,
      selectedTranscription,
      selectedCriteria,
      getLabelForFragment,
      setSelectedFragment,
      setIsEditing,
      setCurrentLabelId,
      setLabelType,
      setReason,
    ]
  );

  return { getLabelForFragment, handleFragmentClick };
};

/**
 * Hook for label operations (create, update, delete)
 */
const useLabelOperations = (
  selectedTranscription: Transcription | null,
  selectedCriteria: Criteria | null,
  formState: ReturnType<typeof useLabelForm>,
  labels: Label[],
  setLabels: Dispatch<SetStateAction<Label[]>>,
  notifyHelpers: ReturnType<typeof useNotificationHelper>,
  currentUser?: User | null
) => {
  const { selectedFragment, labelType, reason, isEditing, currentLabelId, resetForm } = formState;
  const { notify, handleApiError } = notifyHelpers;

  const saveLabel = useCallback(
    async (
      newLabelData: Omit<Label, 'id' | 'createdBy' | 'createdDate'>,
      isUpdate: boolean = false
    ) => {
      if (!selectedFragment) {
        notify('Please select a fragment before submitting', 'error');
        return;
      }

      try {
        if (isUpdate && currentLabelId) {
          // If editing, update the existing label
          const updatedLabel = await api.updateLabel({ ...newLabelData, id: currentLabelId });

          // Update the label in local state
          setLabels(prev =>
            prev.map(label => (label.id === currentLabelId ? updatedLabel : label))
          );
          notify('Label updated successfully', 'success');
        } else {
          // Otherwise add a new label
          const createdLabel = await api.createLabel(newLabelData);
          setLabels(prev => [...prev, createdLabel]);
          notify('Label added successfully', 'success');
        }

        // Reset form after state updates are complete
        setTimeout(() => {
          resetForm();
        }, 0);
      } catch (error) {
        handleApiError(error, 'Error saving label');
      }
    },
    [selectedFragment, currentLabelId, setLabels, notify, handleApiError, resetForm]
  );

  const handleDeleteLabel = useCallback(async () => {
    if (!currentLabelId) return;

    try {
      await api.deleteLabel(currentLabelId);

      // Remove the label from local state
      setLabels(prev => prev.filter(label => label.id !== currentLabelId));

      // Reset form
      resetForm();
      notify('Label deleted successfully', 'success');
    } catch (error) {
      handleApiError(error, 'Error deleting label');
    }
  }, [currentLabelId, setLabels, resetForm, notify, handleApiError]);

  const handleSubmitLabel = useCallback(async () => {
    if (!selectedTranscription || !selectedCriteria || !selectedFragment || !currentUser) {
      return;
    }

    try {
      const newLabel: Omit<Label, 'id' | 'createdBy' | 'createdDate'> = {
        transcriptionId: selectedTranscription.id,
        policyId: selectedCriteria.policyId,
        criteriaId: selectedCriteria.id,
        essayContentFragment: selectedFragment.text,
        fragmentIndex: selectedFragment.index,
        type: labelType,
        reason: reason || null,
      };

      await saveLabel(newLabel, isEditing);
    } catch (error) {
      handleApiError(error, 'Error submitting label');
    }
  }, [
    selectedTranscription,
    selectedCriteria,
    selectedFragment,
    labelType,
    reason,
    isEditing,
    saveLabel,
    handleApiError,
    currentUser,
  ]);

  const handleSubmitWithType = useCallback(
    async (type: LabelType) => {
      // Set the label type first
      if (type === 'uncertain' && !reason.trim()) {
        notify('A reason is required for uncertain labels', 'error');
        return;
      }

      if (!selectedFragment || !selectedTranscription || !selectedCriteria || !currentUser) {
        notify('Please select a fragment before submitting', 'error');
        return;
      }

      const fragmentText = selectedFragment.text.startsWith('\n\n')
        ? selectedFragment.text.substring(2)
        : selectedFragment.text;

      const newLabel: Omit<Label, 'id' | 'createdBy' | 'createdDate'> = {
        transcriptionId: selectedTranscription.id,
        policyId: selectedCriteria.policyId,
        criteriaId: selectedCriteria.id,
        essayContentFragment: fragmentText,
        fragmentIndex: selectedFragment.index,
        type: type,
        reason: reason || null,
      };

      await saveLabel(newLabel, isEditing);
    },
    [
      reason,
      selectedFragment,
      selectedTranscription,
      selectedCriteria,
      saveLabel,
      isEditing,
      notify,
      currentUser,
    ]
  );

  return {
    handleDeleteLabel,
    handleSubmitLabel,
    handleSubmitWithType,
  };
};

/**
 * Hook for handling transcription identifiability operations
 */
const useTranscriptionIdentifiabilityOperations = (
  selectedTranscription: Transcription | null,
  selectedCriteria: Criteria | null,
  resetForm: () => void,
  labels: Label[],
  setLabels: Dispatch<SetStateAction<Label[]>>,
  notifyHelpers: ReturnType<typeof useNotificationHelper>,
  currentUser?: User | null
) => {
  const { notify, handleApiError } = notifyHelpers;

  const handleMarkAsUnidentifiable = useCallback(async () => {
    if (!selectedTranscription || !selectedCriteria || !currentUser) {
      return;
    }

    // Always clear selection when toggling identifiable status
    resetForm();

    // Find labels to delete for this transcription/policy
    const labelsToDelete = labels.filter(
      label =>
        label.transcriptionId === selectedTranscription.id &&
        label.criteriaId === selectedCriteria.id &&
        label.createdBy === currentUser.id
    );

    // Create an unidentifiable label
    const unidentifiableLabel: Omit<Label, 'id' | 'createdBy' | 'createdDate'> = {
      transcriptionId: selectedTranscription.id,
      policyId: selectedCriteria.policyId,
      criteriaId: selectedCriteria.id,
      essayContentFragment: null,
      type: 'unidentifiable',
      reason: null,
    };

    try {
      // Delete existing labels if any
      for (const label of labelsToDelete) {
        await api.deleteLabel(label.id);
      }

      // Create the unidentifiable label
      const data = await api.createLabel(unidentifiableLabel);

      // Update state
      setLabels(prev => [
        ...prev.filter(
          label =>
            !(
              label.transcriptionId === selectedTranscription.id &&
              label.criteriaId === selectedCriteria.id
            )
        ),
        data,
      ]);

      notify('Transcription marked as unidentifiable for this policy', 'success');
    } catch (error) {
      handleApiError(error, 'Error marking transcription as unidentifiable');
    }
  }, [
    selectedTranscription,
    selectedCriteria,
    resetForm,
    labels,
    setLabels,
    notify,
    handleApiError,
    currentUser,
  ]);

  const handleMarkAsIdentifiable = useCallback(async () => {
    if (!selectedTranscription || !selectedCriteria || !currentUser) {
      return;
    }

    // Always clear selection when toggling identifiable status
    resetForm();

    // Find the unidentifiable label
    const unidentifiableLabel = labels.find(
      label =>
        label.transcriptionId === selectedTranscription.id &&
        label.criteriaId === selectedCriteria.id &&
        label.type === 'unidentifiable' &&
        label.createdBy === currentUser.id
    );

    if (!unidentifiableLabel) {
      notify('No unidentifiable label found for this transcription and policy', 'error');
      return;
    }

    try {
      await api.deleteLabel(unidentifiableLabel.id);

      // Remove the unidentifiable label from state
      setLabels(prev => prev.filter(label => label.id !== unidentifiableLabel.id));

      notify('Transcription marked as identifiable again', 'success');
    } catch (error) {
      handleApiError(error, 'Error marking transcription as identifiable');
    }
  }, [
    selectedTranscription,
    selectedCriteria,
    resetForm,
    labels,
    setLabels,
    notify,
    handleApiError,
    currentUser,
  ]);

  return {
    handleMarkAsUnidentifiable,
    handleMarkAsIdentifiable,
  };
};

/**
 * Hook for batch operations on labels
 */
const useBatchOperations = (
  selectedTranscription: Transcription | null,
  selectedCriteria: Criteria | null,
  resetForm: () => void,
  labels: Label[],
  setLabels: Dispatch<SetStateAction<Label[]>>,
  getCleanFragments: (text: string) => string[],
  notifyHelpers: ReturnType<typeof useNotificationHelper>,
  currentUser?: User | null
) => {
  const { notify, handleApiError } = notifyHelpers;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const toggleAllLabels = useCallback(
    async (labelType: LabelType) => {
      if (!selectedTranscription || !selectedCriteria || !currentUser || isLoading) {
        return;
      }

      setIsLoading(true);

      resetForm();

      try {
        const fragments = getCleanFragments(selectedTranscription.content);

        // Find existing labels for this transcription/policy
        const existingLabels = labels.filter(
          label =>
            label.transcriptionId === selectedTranscription.id &&
            label.criteriaId === selectedCriteria.id &&
            label.createdBy === currentUser.id
        );

        // Check if there are any labels of the specified type
        const hasLabelsOfType = existingLabels.some(label => label.type === labelType);

        // If there are any labels of this type, remove all labels
        if (hasLabelsOfType) {
          notify(`Removing all ${labelType} labels...`, 'success');

          // Delete all existing labels
          for (const label of existingLabels) {
            await api.deleteLabel(label.id);
          }

          // Update state by removing the deleted labels
          setLabels(prev =>
            prev.filter(
              label =>
                !(
                  label.transcriptionId === selectedTranscription.id &&
                  label.criteriaId === selectedCriteria.id
                )
            )
          );

          notify(`All ${labelType} labels removed`, 'success');
        } else {
          for (const label of existingLabels) {
            await api.deleteLabel(label.id);
          }

          const batchLabels = [];
          for (const [index, fragment] of fragments.entries()) {
            const newLabel: Omit<Label, 'id' | 'createdBy' | 'createdDate'> = {
              transcriptionId: selectedTranscription.id,
              policyId: selectedCriteria.policyId,
              criteriaId: selectedCriteria.id,
              essayContentFragment: fragment,
              fragmentIndex: index,
              type: labelType,
              reason: null,
            };

            const createdLabel = await api.createLabel(newLabel);
            batchLabels.push(createdLabel);
          }

          setLabels(prev => [
            ...prev.filter(
              label =>
                !(
                  label.transcriptionId === selectedTranscription.id &&
                  label.criteriaId === selectedCriteria.id
                )
            ),
            ...batchLabels,
          ]);

          notify(`All ${fragments.length} fragments labeled as ${labelType}`, 'success');
        }
      } catch (error) {
        handleApiError(error, `Error toggling ${labelType} labels`);
      } finally {
        // Reset loading state when operation completes
        setIsLoading(false);
      }
    },
    [
      selectedTranscription,
      selectedCriteria,
      resetForm,
      labels,
      setLabels,
      getCleanFragments,
      notify,
      handleApiError,
      currentUser,
      isLoading,
    ]
  );

  const handleToggleAllPositive = useCallback(() => {
    if (!isLoading) {
      return toggleAllLabels('positive');
    }
  }, [toggleAllLabels, isLoading]);

  const handleToggleAllNegative = useCallback(() => {
    if (!isLoading) {
      return toggleAllLabels('negative');
    }
  }, [toggleAllLabels, isLoading]);

  return {
    handleToggleAllPositive,
    handleToggleAllNegative,
    isLoading,
  };
};

/**
 * Main hook that combines the smaller hooks and provides label operations
 */
export const useLabels = (
  selectedTranscription: Transcription | null,
  selectedCriteria: Criteria | null,
  setNotification: (notification: NotificationState) => void,
  getFragments: (text: string, granularity: Granularity, clean?: boolean) => string[],
  granularity: Granularity,
  currentUser?: User | null
) => {
  const { labels, setLabels } = useLabelData();
  const { isTranscriptionUnidentifiable } = useTranscriptionIdentifiability(
    selectedTranscription,
    selectedCriteria,
    labels,
    currentUser
  );

  const formState = useLabelForm();
  const { selectedFragment, labelType, reason, setReason, isEditing, resetForm } = formState;

  const notifyHelpers = useNotificationHelper(setNotification);

  const getCleanFragments = useCallback(
    (text: string) => getFragments(text, granularity, true),
    [getFragments, granularity]
  );

  const { getLabelForFragment, handleFragmentClick } = useFragmentInteractions(
    selectedTranscription,
    selectedCriteria,
    labels,
    isTranscriptionUnidentifiable,
    formState,
    getCleanFragments,
    currentUser
  );

  const { handleDeleteLabel, handleSubmitLabel, handleSubmitWithType } = useLabelOperations(
    selectedTranscription,
    selectedCriteria,
    formState,
    labels,
    setLabels,
    notifyHelpers,
    currentUser
  );

  const { handleMarkAsUnidentifiable, handleMarkAsIdentifiable } =
    useTranscriptionIdentifiabilityOperations(
      selectedTranscription,
      selectedCriteria,
      resetForm,
      labels,
      setLabels,
      notifyHelpers,
      currentUser
    );

  const { handleToggleAllPositive, handleToggleAllNegative, isLoading } = useBatchOperations(
    selectedTranscription,
    selectedCriteria,
    resetForm,
    labels,
    setLabels,
    getCleanFragments,
    notifyHelpers,
    currentUser
  );

  return {
    labels,
    isTranscriptionUnidentifiable,
    selectedFragment,
    labelType,
    reason,
    setReason,
    isEditing,
    getLabelForFragment,
    handleFragmentClick,
    handleDeleteLabel,
    handleSubmitLabel,
    handleSubmitWithType,
    handleMarkAsUnidentifiable,
    handleMarkAsIdentifiable,
    handleToggleAllPositive,
    handleToggleAllNegative,
    resetForm,
    isLoading,
  };
};
